<script>
import cloneDeep from "lodash/cloneDeep";
import { mapStores } from "pinia";
import EventDetails from "@/components/EventDetails.vue";
import RadioInputGroup from "@/components/forms/RadioInputGroup";
import { getEventsAmount as returnEventsAmount } from "@/components/methods";
import Steps from "@/components/parts/Steps";
import { format } from "@/libs/date.format";
import getDatesRange from "@/mixins/getDatesRange";
import submitSimpleEvent from "@/mixins/submitSimpleEvent";
import updateImage from "@/mixins/updateImage";
import BookingSettings from "../../../components/BookingSettings.vue";
import EventDates from "@/components/EventDates.vue";
import Actions from "./Actions.vue";
import AddHolidays from "./AddHolidays.vue";
import SimpleEventLeftMenu from "./SimpleEventLeftMenu.vue";
import SimpleEventSummary from "./SimpleEventSummary.vue";
import { useBookingSettingsStore } from "../../../stores/bookingStore";
import TicketsAndPricingV2 from "@/components/TicketsAndPriceV2.vue";
import { usePricingStore } from "../../../stores/pricingStore";
import { isValidEmailFormat } from "../../../libs/is-valid-email-format";

export default {
    name: "AddEventToVenue",
    components: {
        TicketsAndPricingV2,
        SimpleEventSummary,
        AddHolidays,
        SimpleEventLeftMenu,
        Actions,
        Steps,
        EventDetails,
        EventDates,
        RadioInputGroup,
        BookingSettings,
    },
    mixins: [updateImage, submitSimpleEvent, getDatesRange],
    data() {
        return {
            venue: {},
            allVenues: [],
            activeStep: 1,
            activityType: {},
            activity: {
                eventType: "",
                name: "",
                description: "",
                img: "",
                primaryCategory: null,
                secondaryCategories: null,
                bookingEmail: "",
                ages: [],
                bookingType: "",
                bookingLink: "",
            },
            errors: {
                name: "",
                description: "",
                img: "",
                primaryCategory: "",
                bookingEmail: "",
                bookingType: "",
            },
            categories: [],
            time: {
                start: "",
                end: "",
            },
            timeErrors: {
                start: "",
                end: "",
            },
            repeatType: "",
            monthRepeatType: "day",
            addHoliday: "no",
            dates: {
                start: null,
                end: null,
            },
            datesErrors: {
                startDate: "",
                endDate: "",
                startTime: "",
                endTime: "",
                addHoliday: "",
            },
            defaultDates: {
                start: null,
                end: null,
            },
            newHolidays: [],
            holidays: [],
            defaultHolidaysData: [],
            existingHolidays: [],
            priceData: {},
            priceError: {
                price: "",
                freeTrial: "",
            },

            bookingErrors: {},

            showEventDates: false,
            showEventDetails: false,

            showSubmitSpinner: false,

            detailsSubmitted: false,

            fbAuth: null,

            businessId: null,
            isOnMountedFinished: false,
            isRouteChangedFinished: true,
        };
    },
    async mounted() {
        const venues = this.$storage.getStorageSync("venues") || [];

        if (venues.length > 0) {
            this.allVenues = venues;
            const activeVenue = venues[this.$route.params.venueIdx];
            this.venue = activeVenue;
        }

        this.categories = window.allCategories;

        await this.getInitStoreData();

        if (this.$route.name === "Add holidays") {
            this.getExistingHolidays();
        }

        this.isOnMountedFinished = true;
    },
    methods: {
        cloneDeep(item) {
            return cloneDeep(item);
        },

        handleEventDetailsUpdate(details) {
            const { img } = this.activity;
            this.activity = { ...this.activity, ...details };
            if (img !== "") {
                this.activity.img = img;
            }

            if (this.detailsSubmitted) {
                this.validate();
            }
        },

        async getInitStoreData() {
            const activity = this.$storage.getStorageSync("activity");

            if (activity) {
                this.activity = activity;
            } else {
                this.activity = {
                    eventType: "",
                    name: "",
                    description: "",
                    img: "",
                    primaryCategory: null,
                    secondaryCategories: null,
                    bookingEmail: window.businessEmail,
                    ages: [],
                    bookingType: "",
                };
            }

            this.imgData = this.$storage.getStorageSync("imgData") || [];
            this.activityType = this.$storage.getStorageSync("activityType");

            this.dates = this.$storage.getStorageSync("dates") || { start: null, end: null };
            this.defaultDates = cloneDeep(this.dates);
            this.holidays = this.$storage.getStorageSync("holidays") || [];
            this.defaultHolidaysData = cloneDeep(this.holidays);
            this.time = this.$storage.getStorageSync("time") || { start: "", end: "" };
            this.repeatType = this.$storage.getStorageSync("repeatType") || "";
            this.monthRepeatType = this.$storage.getStorageSync("monthRepeatType") || "day";

            this.priceData = this.$storage.getStorageSync("priceData") || {};

            this.addHoliday = this.$storage.getStorageSync("addHoliday") || "no";
            this.holidays = this.$storage.getStorageSync("holidays") || [];

            this.showEventDates = true;
            this.showEventDetails = true;

            await this.pricingStore.getLocalStorage(this.$storage);
            await this.bookingSettingsStore.getLocalStorage(this.$storage);
        },

        priceUpdated(price) {
            this.priceData = price;
        },

        bookingUpdated(data) {
            this.bookingData = data;
        },

        updateHolidays(holidays) {
            this.holidays = holidays;
        },

        isInputEmpty(input) {
            return input === "" || input === undefined || input === null || input === false;
        },

        validateDates() {
            this.datesErrors.startDate = (Number.isNaN(Date.parse(this.dates.start.plain)))
                ? "Choose a first date"
                : "";

            this.datesErrors.endDate = (Number.isNaN(Date.parse(this.dates?.end?.plain))) && this.repeatType !== "no-repeat"
                ? "Choose a last date"
                : "";

            this.datesErrors.startTime = (this.time.start === "" || this.time.start === null)
                ? "Choose a start time"
                : "";

            this.datesErrors.endTime = (this.time.end === "" || this.time.end === null)
                ? "Choose an end time"
                : "";

            this.datesErrors.addHoliday = (this.addHoliday === "")
                ? "Please select one of the checkboxes"
                : "";

            const errors = Object.values(this.datesErrors).filter((el) => el !== "");

            return errors.length <= 0;
        },

        async submitForm() {
            if (this.$route.name === "Add event to venue") {
                this.submit();
            }

            if (this.$route.name === "Add dates") {
                const validDateForm = this.validateDates();
                if (!validDateForm) {
                    return;
                }

                if (this.dates?.end?.plain) {
                    this.dates.name = `${format(this.dates.start.plain, "dS mmmm yyyy")} - `
                        + `${format(this.dates.end.plain, "dS mmmm yyyy")}`;
                } else {
                    this.dates.name = `${format(this.dates.start.plain, "dS mmmm yyyy")}`;
                }

                if (this.dates.end === undefined) {
                    this.dates.end = this.dates.start;
                }

                this.$storage.setStorageSync("dates", this.dates);
                this.$storage.setStorageSync("time", this.time);
                this.$storage.setStorageSync("repeatType", this.repeatType);
                this.$storage.setStorageSync("monthRepeatType", this.monthRepeatType);
                this.$storage.setStorageSync("addHoliday", this.addHoliday);

                if (this.addHoliday === "yes" && this.repeatType !== "no-repeat") {
                    this.getExistingHolidays();
                    this.$router.push({ name: "Add holidays" });
                } else if (
                    this.addHoliday === "no" || this.repeatType === "no-repeat"
                ) {
                    this.$router.push({ name: "Add pricing" });
                }
            }

            if (this.$route.name === "Add holidays") {
                // Go to the next step even if holidays is empty
                this.$storage.setStorageSync("holidays", this.holidays);
                this.$router.push({ name: "Add pricing" });
            }

            if (this.$route.name === "Add pricing") {
                if (!this.pricingStore.validate()) {
                    return;
                }

                await this.pricingStore.setLocalStorage(this.$storage);
                if (this.pricingStore.sellingTicket) {
                    this.$router.push({ name: "Booking settings" });
                } else {
                    this.$router.push({ name: "Simple event summary" });
                }
            }

            if (this.$route.name === "Booking settings") {
                if (!this.bookingSettingsStore.validate()) {
                    return;
                }
                await this.bookingSettingsStore.setLocalStorage(this.$storage);
                this.$router.push({ name: "Simple event summary" });
            }

            if (this.$route.name === "Simple event summary") {
                this.showSubmitSpinner = true;
                await this.submitSimpleEventFlow();
                this.showSubmitSpinner = false;
            }
        },

        async prevStep() {
            switch (this.$route.name) {
            case "Add pricing":
                if (this.addHoliday === "yes" && this.repeatType !== "no-repeat") {
                    this.$router.push({ name: "Add holidays" });
                } else {
                    this.$router.push({ name: "Add dates" });
                }
                break;
            case "Add dates":
                this.$router.push({ name: "Add event to venue" });
                break;

            case "Add event to venue":
                this.$router.push({ name: "Simple venues" });
                break;

            case "Add holidays":
                this.$router.push({ name: "Add dates" });
                break;

            case "Booking settings":
                this.$router.push({ name: "Add pricing" });
                break;

            case "Simple event summary":
                if (this.pricingStore.sellingTicket) {
                    this.$router.push({ name: "Booking settings" });
                } else {
                    this.$router.push({ name: "Add pricing" });
                }
                break;
            default:
                break;
            }
            await this.getInitStoreData();
        },

        async setImg(img) {
            const oldImg = this.imgData.find((old) => old.imgId === this.activity.img);
            const updatedImage = await this.updateImage(img, window.currentUserId, oldImg);
            const newImgData = this.imgData.filter((d) => d.imgId !== this.activity.img);
            newImgData.push(updatedImage);
            this.activity.img = updatedImage.imgId;
            this.syncStorageForImgData(newImgData);
            this.$storage.setStorageSync("activity", this.activity);
        },

        // eslint-disable-next-line consistent-return
        async submit() {
            let validName = false;

            const valid = this.validate();
            if (valid) {
                const response = await this.checkActivityName();

                if (response.data === "false" && response.errors.name) {
                    this.errors.name = response.errors.name;
                } else {
                    validName = true;
                }
            } else {
                return false;
            }

            if (validName) {
                if (this.activity.primaryCategory === this.activity.secondaryCategories) {
                    this.activity.secondaryCategories = null;
                }

                this.$storage.setStorageSync("activity", this.activity);
                await this.$router.push({ name: "Add dates" });
            }
            this.detailsSubmitted = true;
        },
        checkActivityName() {
            // TODO: jquery! :(
            // eslint-disable-next-line no-undef
            return $.ajax({
                url: "/check-unique-activity-name-for-user",
                method: "POST",
                data: {
                    activityName: this.activity.name,
                    userId: window.currentUserId,
                },
                dataType: "json",
            });
        },
        validate() {
            this.errors.eventType = (this.activity.eventType === "")
                ? "Choose an event type"
                : "";
            this.errors.ages = (this.activity.ages.length === 0)
                ? "Choose an age"
                : "";
            this.errors.name = (this.activity.name === "")
                ? "Enter an event name"
                : "";

            this.errors.description = (this.activity.description === "")
                ? "Enter a description"
                : "";
            this.errors.img = (this.activity.img === "")
                ? "Choose a picture"
                : "";
            this.errors.primaryCategory = (
                this.activity.primaryCategory?.name === ""
                || this.activity.primaryCategory?.name === null
                || this.activity.primaryCategory?.name === undefined
            )
                ? "Choose a category"
                : "";
            this.errors.bookingEmail = (
                this.activity.bookingEmail === ""
                || this.activity.bookingEmail === null
                || !isValidEmailFormat(this.activity.bookingEmail)
            )
                ? "Enter a booking email"
                : "";
            this.errors.bookingType = (this.activity.bookingType === "")
                ? "Choose an attendance type"
                : this.errors.bookingType = "";

            const errors = Object.values(this.errors).filter((el) => el !== "");

            if (errors.length > 0) {
                return false;
            }
            return true;
        },

        getExistingHolidays() {
            const formData = new FormData();

            formData.append("userId", window.currentUserId);
            formData.append("startDate", JSON.stringify(this.dates.start));
            formData.append("endDate", JSON.stringify(this.dates.end));

            fetch("/get-existing-holidays", {
                method: "POST",
                body: formData,
            }).then((res) => res.json()).then((data) => {
                if (data.success) {
                    this.existingHolidays = data.data;
                } else {
                    alert("Form submitted with errors");
                }
            });
        },

        nthWeekdayOfMonth(weekday, day, date) {
            let count = 0;
            const dayNumber = ["First", "Second", "Third", "Fourth", "Fifth"];
            const idate = new Date(date.getFullYear(), date.getMonth(), 1);
            while (true) {
                if (idate.getDay() === weekday) {
                    if (idate.getDate() === day) {
                        break;
                    }
                    count += 1;
                }
                idate.setDate(idate.getDate() + 1);
            }

            return dayNumber[count];
        },

        setUpdatedEventDates(data) {
            this.dates = data.dates;
            this.time = data.times;
            this.monthRepeatType = data.monthRepeatType;
            this.repeatType = data.repeatType;
        },
    },
    computed: {
        ...mapStores(useBookingSettingsStore),
        ...mapStores(usePricingStore),
        descriptionCharLeft() {
            return 300 - this.activity.description.length;
        },

        getActivityImg() {
            return this.imgData.find((images) => images.imgId === this.activity.img)?.imgUrl || "";
        },

        enquiryLabelText() {
            if (this.activity.bookingWith === "email" && this.activity.bookingEmail !== "") {
                return "E-mail address";
            }

            if (this.activity.bookingWith === "url" && this.activity.bookingUrl !== "") {
                return "Url";
            }

            return "";
        },

        getCurrentStep() {
            const steps = [
                "Add event to venue",
                "Add dates",
                "Add holidays",
                "Add pricing",
                "Booking settings",
                "Simple event summary",
            ];

            const idx = steps.findIndex((el) => el === this.$route.name);

            // Normalize step count
            let currentStep = idx + 1;

            if (idx >= 3 && (this.repeatType === "no-repeat" || this.addHoliday === "no")) {
                currentStep -= 1;
            }

            if (idx > 4 && this.pricingStore.sellingTicket) {
                currentStep -= 1;
            }

            return currentStep;
        },

        getTotalSteps() {
            let totalSteps = 6;

            if (this.addHoliday === "no" || this.repeatType === "no-repeat") {
                totalSteps -= 1;
            }

            if (this.pricingStore.sellingTicket === false) {
                totalSteps -= 1;
            }

            return totalSteps;
        },

        getEventsAmountNumber() {
            return returnEventsAmount(
                new Date(this.dates.start?.plain),
                new Date(this.dates.end?.plain),
                this.repeatType,
                this.holidays,
            );
        },

        getEventsAmount() {
            const amount = this.getEventsAmountNumber.value;

            if (amount === "") {
                return "";
            }

            return `${amount} ${this.activity.eventType.name?.toLowerCase()
            || this.activity.eventType.toLowerCase()
            } dates`;
        },

    },
    watch: {
        activity: {
            deep: true,
            handler() {
                if (this.activity.name !== "") {
                    this.errors.name = "";
                }

                if (this.activity.description !== "") {
                    this.errors.description = "";
                }

                if (this.activity.img !== "") {
                    this.errors.img = "";
                }

                if (this.activity.primaryCategory?.category_id !== "") {
                    this.errors.primaryCategory = "";
                }

                if (this.activity.bookingWith === "email" && this.activity.bookingEmail !== "") {
                    this.errors.bookingEnquiries = "";
                }

                if (this.activity.bookingWith === "url" && this.activity.bookingUrl !== "") {
                    this.errors.bookingEnquiries = "";
                }
            },
        },
        async $route(to) {
            this.isRouteChangedFinished = false;
            await this.getInitStoreData();

            if (to.name === "Add holidays") {
                const startDate = Date.parse(this.dates.start.plain);
                const endDate = Date.parse(this.dates.end.plain);

                const filteredHolidays = this.holidays.filter((holiday) => {
                    if (
                        (Date.parse(holiday.start.plain) > startDate)
                        && (Date.parse(holiday.end.plain) < endDate)
                    ) {
                        return true;
                    }
                    return false;
                });

                this.holidays = filteredHolidays;
            }
            this.isRouteChangedFinished = true;
        },
    },

};
</script>

<template>
    <div class="d-flex flex-row position-relative add-event-to-venue-container"
         v-if="isOnMountedFinished && isRouteChangedFinished">
        <SimpleEventLeftMenu
            :all-venues="allVenues"
            :venue-name="venue.venueName"
            :event-type="activity.eventType.name || activity.eventType" :step="getCurrentStep"
            :show-holidays="addHoliday === 'yes' && repeatType !== 'no-repeat'"
            :sell-tickets="pricingStore.sellingTicket"
            :is-free="false"
            :route="$route.name"
            class="position-absolute"
        />

        <div ref="mainBlock" class="mx-auto">
            <div class="simple-event-container add-event-to-venue">

                <Steps :current-step="getCurrentStep" :total-steps="getTotalSteps"/>

                <div v-if="$route.name === 'Add event to venue'" class="">
                    <h2 class="title" data-test="page-title">
                        {{ activity.eventType.name || activity.eventType || "Event" }}
                        details
                    </h2>
                    <EventDetails v-if="showEventDetails" @updated="handleEventDetailsUpdate" @set-img="setImg"
                                  :default="{

                                      ...cloneDeep(activity),
                                      img: getActivityImg,
                                  }" :errors="errors"/>
                </div>
                <div v-if="$route.name === 'Add dates'">

                    <EventDates
                        v-if="showEventDates"
                        :event-type="activity.eventType.name || activity.eventType"
                        :default-dates="defaultDates"
                        :default-times="time"
                        :default-repeat-type="repeatType"
                        :default-month-repeat-type="monthRepeatType"
                        :default-holidays="[]"
                        :errors="datesErrors"
                        @updated="setUpdatedEventDates"/>

                    <div v-if="repeatType !== 'no-repeat'" data-test="block-add-holiday">
                        <div class="small-title holidays-spacing">Would you like to add a holiday?</div>
                        <div class="d-flex flex-column">
                            <div class="mt-2">
                                <RadioInputGroup label="title" v-model="addHoliday" :selected="addHoliday" :list="[
                                    {
                                        title: 'Yes',
                                        value: 'yes',
                                        dataTest: 'radio-holiday-yes',
                                    },
                                    {
                                        title: 'No',
                                        value: 'no',
                                        dataTest: 'radio-holiday-no',
                                    },
                                ]"/>
                            </div>
                        </div>
                    </div>

                    <div :class="{ 'error-text': datesErrors.addHoliday !== '' }">
                        {{ datesErrors.addHoliday }}
                    </div>
                </div>

                <div v-if="$route.name === 'Add holidays'">
                    <AddHolidays :pre-existing="existingHolidays" @holidays-updated="updateHolidays"/>
                </div>

                <div v-if="$route.name === 'Add pricing'">
                    <TicketsAndPricingV2
                        :event-type="activity.eventType.name || activity.eventType"
                        :recurring-type="this.repeatType"
                        :all-events-amount="getEventsAmountNumber"
                    />
                </div>

                <div v-if="$route.name === 'Booking settings'">
                    <BookingSettings :storage="$storage"/>
                </div>

                <div v-if="$route.name === 'Simple event summary'">
                    <SimpleEventSummary
                        :act-data="activity"
                        :dates="dates"
                        :times="time"
                        :repeat="repeatType"
                        :price="priceData"
                        :add-holiday="addHoliday"
                        :holidays="holidays"
                        :price-v2="pricingStore.getPricingState"
                    />
                </div>

            </div>

            <Actions class="fixed-bottom" :full-width="true" actions-styles="alt-bottom-actions" @prev="prevStep"
                     @next="submitForm" :next-button-text="$route.name === 'Simple event summary'
                         ? 'Save and publish'
                         : 'Next'" :margin-top="$route.name === 'Add dates'
                         ? 'margin-top-dates'
                         : ''"
                     :show-next-button-spinner="$route.name === 'Simple event summary' && showSubmitSpinner"
                     :disabled="false"/>
        </div>
    </div>
</template>

<style scoped>
.title {
    margin-bottom: 3rem;
}

.add-event-to-venue {
    margin: 0 auto;
}

.small-title {
    font-weight: 600;
}

.holidays-spacing {
    margin-top: 3rem;
}

@media only screen and (max-width: 768px) {
    .add-event-to-venue-container {
        flex-direction: column !important;
    }
}
</style>
